import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Copy, Megaphone, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

export default function AdminCatalogBroadcastCard({ produits: produitsProp = [] }) {
  const [produits, setProduits] = useState(produitsProp);
  const [secteur, setSecteur] = useState("all");
  const [selectedProduitIds, setSelectedProduitIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    if (produitsProp.length > 0) {
      setProduits(produitsProp);
      return;
    }
    api
      .get("/produits")
      .then(({ data }) => setProduits(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [produitsProp]);

  const secteurs = useMemo(
    () => [...new Set(produits.map((p) => p.secteur).filter(Boolean))].sort((a, b) => a.localeCompare(b, "fr")),
    [produits],
  );

  const produitsDuSecteur = useMemo(() => {
    if (!secteur || secteur === "all") return produits;
    return produits.filter((p) => p.secteur === secteur);
  }, [produits, secteur]);

  const toggleProduit = (id) => {
    const pid = String(id);
    setSelectedProduitIds((prev) =>
      prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid],
    );
  };

  const selectAllProduits = () => {
    setSelectedProduitIds(produitsDuSecteur.map((p) => String(p.produit_id)));
  };

  const clearProduits = () => setSelectedProduitIds([]);

  useEffect(() => {
    setSelectedProduitIds([]);
  }, [secteur]);

  const generate = useCallback(async () => {
    setLoading(true);
    try {
      const params = { secteur: secteur || "all", _refresh: Date.now() };
      if (selectedProduitIds.length > 0) {
        params.produit_ids = selectedProduitIds.join(",");
      }
      const { data } = await api.get("/admin/catalog-broadcast", { params, headers: { "Cache-Control": "no-cache" } });
      const digest = data?.data ?? data;
      setMessage(String(digest?.message || ""));
      setMeta(digest?.meta ?? null);
      if (!digest?.message) {
        toast.warning("Aucune annonce pour cette sélection.");
      } else {
        toast.success("Message généré");
      }
    } catch (e) {
      setMessage("");
      setMeta(null);
      toast.error(e?.response?.data?.detail || e?.message || "Impossible de générer le message");
    } finally {
      setLoading(false);
    }
  }, [secteur, selectedProduitIds]);

  const copyMessage = async () => {
    if (!message) return;
    try {
      await navigator.clipboard.writeText(message);
      toast.success("Message copié — prêt à publier");
    } catch {
      toast.error("Copie impossible sur ce navigateur");
    }
  };

  const filterHint =
    selectedProduitIds.length > 0
      ? `${selectedProduitIds.length} produit(s) sélectionné(s)`
      : secteur && secteur !== "all"
        ? `Tous les produits du secteur « ${secteur} »`
        : "Tout le catalogue en ligne";

  return (
    <section
      className="rounded-2xl border border-sky-200/90 bg-gradient-to-br from-sky-50/80 to-white p-4 shadow-sm sm:p-5"
      data-testid="admin-catalog-broadcast-card"
    >
      <div className="mb-4 flex flex-wrap items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#2D7FF9] text-white shadow-sm">
          <Megaphone className="h-5 w-5" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold text-slate-900">Message de diffusion catalogue</h3>
          <p className="mt-0.5 text-xs text-slate-600">
            Inventaire des annonces en ligne (produit et départements) avec lien vers rdvmarket.fr. Générez puis
            copiez le texte pour vos canaux de communication.
          </p>
        </div>
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-600">Secteur</Label>
          <Select value={secteur} onValueChange={setSecteur}>
            <SelectTrigger className="h-10 bg-white text-sm" data-testid="admin-broadcast-filter-secteur">
              <SelectValue placeholder="Tous les secteurs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les secteurs</SelectItem>
              {secteurs.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="bg-white"
            onClick={selectAllProduits}
            disabled={!produitsDuSecteur.length}
          >
            Tout sélectionner
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={clearProduits}>
            Aucun
          </Button>
        </div>
      </div>

      {produitsDuSecteur.length > 0 ? (
        <div className="mb-4 max-h-36 overflow-y-auto rounded-lg border border-slate-200/90 bg-white p-2">
          <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Produits (vide = tous dans le secteur)
          </p>
          <ul className="space-y-1">
            {produitsDuSecteur.map((p) => {
              const pid = String(p.produit_id);
              const checked = selectedProduitIds.includes(pid);
              return (
                <li key={pid}>
                  <label
                    className={cn(
                      "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-sky-50/80",
                      checked && "bg-sky-50/90",
                    )}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-[#2D7FF9]"
                      checked={checked}
                      onChange={() => toggleProduit(pid)}
                      data-testid={`admin-broadcast-produit-${pid}`}
                    />
                    <span className="min-w-0 flex-1 truncate font-medium text-slate-800">{p.nom}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      <p className="mb-3 text-[11px] text-slate-500">
        Filtre actif : <strong>{filterHint}</strong>
        {meta ? (
          <>
            {" "}
            · {meta.totalListings} annonce{meta.totalListings > 1 ? "s" : ""} · {meta.productCount} produit
            {meta.productCount > 1 ? "s" : ""}
            {meta.marketplaceUrl ? (
              <>
                {" "}
                · lien <span className="font-mono text-[11px] text-slate-700">{meta.marketplaceUrl}</span>
              </>
            ) : null}
          </>
        ) : null}
      </p>

      <div className="mb-3 flex flex-wrap gap-2">
        <Button
          type="button"
          className="bg-[#2D7FF9] hover:bg-[#2568E8]"
          disabled={loading}
          onClick={() => void generate()}
          data-testid="admin-catalog-broadcast-generate"
        >
          <Sparkles className={cn("mr-1 h-4 w-4", loading && "animate-pulse")} aria-hidden />
          {loading ? "Génération…" : "Générer le message"}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="bg-white"
          disabled={!message || loading}
          onClick={() => void copyMessage()}
          data-testid="admin-catalog-broadcast-copy"
        >
          <Copy className="mr-1 h-4 w-4" aria-hidden />
          Copier le texte
        </Button>
      </div>

      <Textarea
        readOnly
        value={message}
        placeholder="Choisissez un secteur et/ou des produits, puis cliquez sur « Générer le message »."
        className="min-h-[200px] resize-y bg-white/90 text-sm leading-relaxed"
        data-testid="admin-catalog-broadcast-text"
      />
    </section>
  );
}
